/**
 * Sidebar icons.
 *
 * *** WHY NOT A UNICODE GLYPH ***
 * The nav previously used geometric characters — `◑` for Mentors, `◍` for
 * Students. They carry no meaning, render differently on every platform, and a
 * half-filled circle tells a reader nothing about people. These are line icons
 * that depict what they label.
 *
 * Inline SVG rather than an icon package: eleven icons do not justify a
 * dependency, and these inherit `currentColor` so the active/hover states in
 * AppShell.module.css keep working untouched.
 */
import type { ReactElement } from 'react'

/** Shared attributes — 24px grid, 1.7 stroke, inheriting the text colour. */
const svg = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  width: '100%',
  height: '100%',
}

export type NavIconName =
  | 'dashboard'
  | 'bookings'
  | 'students'
  | 'mentors'
  | 'payments'
  | 'feedback'
  | 'categories'
  | 'sessionTypes'
  | 'pricing'
  | 'feeHeads'
  | 'chatPolicy'
  | 'settings'

const ICONS: Record<NavIconName, ReactElement> = {
  // Panels — the summary view.
  dashboard: (
    <svg {...svg}>
      <rect x="3" y="3" width="7.5" height="9" rx="1.5" />
      <rect x="13.5" y="3" width="7.5" height="5.5" rx="1.5" />
      <rect x="3" y="15" width="7.5" height="6" rx="1.5" />
      <rect x="13.5" y="11.5" width="7.5" height="9.5" rx="1.5" />
    </svg>
  ),

  // Calendar with a checked day — a booked session.
  bookings: (
    <svg {...svg}>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
      <path d="m9 15 2 2 4-4" />
    </svg>
  ),

  // Two people — a cohort.
  students: (
    <svg {...svg}>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 20a6 6 0 0 1 12 0" />
      <path d="M16.5 5.5a3.2 3.2 0 0 1 0 6M18 20a5.6 5.6 0 0 0-2.4-4.6" />
    </svg>
  ),

  // One person wearing a graduation cap — a mentor, not just any user.
  mentors: (
    <svg {...svg}>
      <path d="M12 3 3.5 7 12 11l8.5-4L12 3Z" />
      <path d="M7 9v3.5c0 1.4 2.2 2.5 5 2.5s5-1.1 5-2.5V9" />
      <path d="M4 20a5.5 5.5 0 0 1 5.5-4h5A5.5 5.5 0 0 1 20 20" />
    </svg>
  ),

  // Banknote.
  payments: (
    <svg {...svg}>
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.6" />
      <path d="M6 12h.01M18 12h.01" />
    </svg>
  ),

  // Star — a rating.
  feedback: (
    <svg {...svg}>
      <path d="m12 3.5 2.6 5.3 5.9.9-4.2 4.1 1 5.8-5.3-2.8-5.3 2.8 1-5.8L3.5 9.7l5.9-.9L12 3.5Z" />
    </svg>
  ),

  // Tagged folders — grouping.
  categories: (
    <svg {...svg}>
      <rect x="3" y="4" width="8" height="8" rx="1.6" />
      <rect x="13" y="4" width="8" height="8" rx="1.6" />
      <rect x="3" y="14" width="8" height="6" rx="1.6" />
      <rect x="13" y="14" width="8" height="6" rx="1.6" />
    </svg>
  ),

  // Clock — a session has a duration.
  sessionTypes: (
    <svg {...svg}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 1.8" />
    </svg>
  ),

  // Price tag.
  pricing: (
    <svg {...svg}>
      <path d="M20.5 12.3 12.8 20a1.7 1.7 0 0 1-2.4 0l-7-7a1.7 1.7 0 0 1-.5-1.2V4.9c0-.9.8-1.7 1.7-1.7h6.9c.4 0 .9.2 1.2.5l7 7c.6.7.6 1.8-.2 2.6Z" />
      <circle cx="7.8" cy="7.8" r="1.4" />
    </svg>
  ),

  // Stacked receipt lines — charges added on top of a base.
  feeHeads: (
    <svg {...svg}>
      <path d="M5 3.5h14v17l-2.3-1.6-2.3 1.6-2.4-1.6-2.3 1.6L7.3 19 5 20.5v-17Z" />
      <path d="M9 8.5h6M9 12.5h6" />
    </svg>
  ),

  // Speech bubble with a rule line — chat, governed.
  chatPolicy: (
    <svg {...svg}>
      <path d="M20.5 12c0 4-3.8 7.2-8.5 7.2a10 10 0 0 1-2.6-.34L4.5 20.5l1.2-3.4A6.9 6.9 0 0 1 3.5 12C3.5 8 7.3 4.8 12 4.8s8.5 3.2 8.5 7.2Z" />
      <path d="M9 11h6M9 14h3.5" />
    </svg>
  ),

  settings: (
    <svg {...svg}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5v.2a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1h.2a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
    </svg>
  ),
}

export function NavIcon({ name }: { name: NavIconName }) {
  return ICONS[name]
}
